import { useEffect, useState } from "react";
import { AlertTriangle, Mail, MapPin, Phone } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Btn, Loading, PageHero, SectionHead } from "@/components/public/Ui";
import {
  ConsentCheck,
  Field,
  FormBanner,
  Honeypot,
  SelectInput,
  TextArea,
  TextInput,
} from "@/components/public/FormKit";
import { useSite } from "@/context/SiteContext";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  message: "",
  subject: "",
  support_type: "",
  island: "",
  interests: [],
  consent: false,
  honeypot: "",
};

function useFormPage(slug, endpoint) {
  const [page, setPage] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  useEffect(() => {
    api.get(`/public/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(false));
  }, [slug]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (validate, payload) => {
    setBanner({ status: "idle", message: "" });
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setBanner({ status: "loading", message: "" });
    try {
      const { data } = await api.post(endpoint, payload);
      setBanner({ status: "ok", message: data.message });
      setForm(EMPTY);
    } catch (err) {
      setBanner({ status: "error", message: apiError(err) });
    }
  };

  return { page, form, setForm, errors, banner, set, submit };
}

const emailOk = (v) => /^\S+@\S+\.\S+$/.test(v);

/* ------------------------------------------------------------- Get Support */

export function GetSupport() {
  const { page, form, setForm, errors, banner, set, submit } = useFormPage("get-support", "/public/forms/support");
  if (page === null) return <Loading />;
  const b = page?.body_json || {};
  const hero = b.hero || {};

  const onSubmit = (ev) => {
    ev.preventDefault();
    submit(
      () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Please enter your name.";
        if (!emailOk(form.email)) e.email = "Please enter a valid email address.";
        if (!form.support_type) e.support_type = "Please choose the type of support you need.";
        if (form.message.trim().length < 10) e.message = "Please tell us a little more.";
        if (!form.consent) e.consent = "Please acknowledge the privacy notice to continue.";
        return e;
      },
      {
        name: form.name,
        email: form.email,
        phone: form.phone,
        support_type: form.support_type,
        island: form.island,
        message: form.message,
        consent: form.consent,
        honeypot: form.honeypot,
      }
    );
  };

  return (
    <>
      <Seo title={page?.seo_title} description={page?.seo_description} canonicalPath="/get-support" />
      <PageHero
        overline={hero.overline}
        line1={hero.heading_line_1}
        line2={hero.heading_line_2}
        subheading={hero.subheading}
        image={mediaUrl(page?.hero_image_url)}
      />
      <div className="bka-container grid gap-12 py-14 lg:grid-cols-[1.25fr_1fr] lg:gap-16 lg:py-20">
        <div>
          <SectionHead overline="Support Request" title="How Can We Help?" text={b.intro} />
          <form onSubmit={onSubmit} className="space-y-5" data-testid="support-form" noValidate>
            <Honeypot value={form.honeypot} onChange={set("honeypot")} />
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Your Name" htmlFor="s-name" required error={errors.name}>
                <TextInput id="s-name" data-testid="support-name" value={form.name} onChange={set("name")} error={errors.name} placeholder="Enter your full name" />
              </Field>
              <Field label="Email Address" htmlFor="s-email" required error={errors.email}>
                <TextInput id="s-email" type="email" data-testid="support-email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@example.com" />
              </Field>
              <Field label="Phone Number" htmlFor="s-phone">
                <TextInput id="s-phone" data-testid="support-phone" value={form.phone} onChange={set("phone")} placeholder="(242) 000-0000" />
              </Field>
              <Field label="Island / Location" htmlFor="s-island">
                <TextInput id="s-island" data-testid="support-island" value={form.island} onChange={set("island")} placeholder="e.g. Nassau" />
              </Field>
            </div>
            <Field label="Type of Support Needed" htmlFor="s-type" required error={errors.support_type}>
              <SelectInput id="s-type" data-testid="support-type" value={form.support_type} onChange={set("support_type")} error={errors.support_type}>
                <option value="">Select the type of support</option>
                {(b.support_types || []).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </SelectInput>
            </Field>
            <Field
              label="Your Message"
              htmlFor="s-message"
              required
              error={errors.message}
              hint="Please do not include detailed medical records."
            >
              <TextArea id="s-message" data-testid="support-message" value={form.message} onChange={set("message")} error={errors.message} rows={6} placeholder="Tell us how we can help…" />
            </Field>
            <ConsentCheck
              id="s-consent"
              testId="support-consent"
              checked={form.consent}
              onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
              error={errors.consent}
            >
              I understand this form is not an emergency service and that my details will be used only to
              respond to my request.
            </ConsentCheck>
            <FormBanner status={banner.status} message={banner.message} testId="support-banner" />
            <Btn testId="support-submit" type="submit" disabled={banner.status === "loading"}>
              {banner.status === "loading" ? "Sending…" : "Send Request"}
            </Btn>
          </form>
        </div>

        <aside className="space-y-6">
          <div
            data-testid="support-emergency-notice"
            className="rounded-2xl border-l-4 border-red-400 bg-red-50 p-6"
            role="note"
          >
            <p className="mb-2 flex items-center gap-2 bka-overline text-red-600">
              <AlertTriangle className="h-4 w-4" /> Not An Emergency Service
            </p>
            <p className="text-sm leading-[1.8] text-red-900/85">{b.notice}</p>
          </div>
          <div className="rounded-2xl bg-bka-aqua p-6">
            <h3 className="font-display text-lg font-bold text-bka-navy">Medication Assistance</h3>
            <p className="mt-2 text-sm text-bka-ink/80">
              Looking for help accessing medication? Read about the program before you enquire.
            </p>
            <div className="mt-5">
              <Btn to="/medication-assistance" variant="outline" testId="support-medication-cta">
                Learn More
              </Btn>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}

/* ----------------------------------------------------------- Get Involved */

export function GetInvolved() {
  const { page, form, setForm, errors, banner, set, submit } = useFormPage("get-involved", "/public/forms/volunteer");
  if (page === null) return <Loading />;
  const b = page?.body_json || {};
  const hero = b.hero || {};

  const toggleInterest = (value) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(value)
        ? f.interests.filter((x) => x !== value)
        : [...f.interests, value],
    }));

  const onSubmit = (ev) => {
    ev.preventDefault();
    submit(
      () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Please enter your name.";
        if (!emailOk(form.email)) e.email = "Please enter a valid email address.";
        if (form.interests.length === 0) e.interests = "Please choose at least one way you'd like to help.";
        return e;
      },
      {
        name: form.name,
        email: form.email,
        phone: form.phone,
        island: form.island,
        interests: form.interests,
        message: form.message,
        consent: form.consent,
        honeypot: form.honeypot,
      }
    );
  };

  return (
    <>
      <Seo title={page?.seo_title} description={page?.seo_description} canonicalPath="/get-involved" />
      <PageHero
        overline={hero.overline}
        line1={hero.heading_line_1}
        line2={hero.heading_line_2}
        subheading={hero.subheading}
        image={mediaUrl(page?.hero_image_url)}
      />
      <div className="bka-container max-w-2xl py-14 lg:py-20">
        <SectionHead overline="Volunteer & Partner" title="Join The Mission" text={b.intro} />
        <form onSubmit={onSubmit} className="space-y-5" data-testid="volunteer-form" noValidate>
          <Honeypot value={form.honeypot} onChange={set("honeypot")} />
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Your Name" htmlFor="v-name" required error={errors.name}>
              <TextInput id="v-name" data-testid="volunteer-name" value={form.name} onChange={set("name")} error={errors.name} placeholder="Enter your full name" />
            </Field>
            <Field label="Email Address" htmlFor="v-email" required error={errors.email}>
              <TextInput id="v-email" type="email" data-testid="volunteer-email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@example.com" />
            </Field>
            <Field label="Phone Number" htmlFor="v-phone">
              <TextInput id="v-phone" data-testid="volunteer-phone" value={form.phone} onChange={set("phone")} placeholder="(242) 000-0000" />
            </Field>
            <Field label="Island / Location" htmlFor="v-island">
              <TextInput id="v-island" data-testid="volunteer-island" value={form.island} onChange={set("island")} placeholder="e.g. Grand Bahama" />
            </Field>
          </div>

          <fieldset>
            <legend className="mb-3 block text-sm font-bold text-bka-navy">
              How would you like to help? <span className="text-red-500">*</span>
            </legend>
            <div className="grid gap-2.5 sm:grid-cols-2">
              {(b.interests || []).map((i) => (
                <label
                  key={i}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 text-sm transition-colors ${
                    form.interests.includes(i)
                      ? "border-bka-emerald bg-bka-aqua font-semibold text-bka-teal"
                      : "border-slate-200 text-bka-ink/85 hover:border-bka-emerald/50"
                  }`}
                >
                  <input
                    type="checkbox"
                    data-testid={`volunteer-interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    checked={form.interests.includes(i)}
                    onChange={() => toggleInterest(i)}
                    className="h-4 w-4 accent-[color:var(--bka-emerald)]"
                  />
                  {i}
                </label>
              ))}
            </div>
            {errors.interests && (
              <p className="mt-2 text-xs font-semibold text-red-600">{errors.interests}</p>
            )}
          </fieldset>

          <Field label="Anything else we should know?" htmlFor="v-message">
            <TextArea id="v-message" data-testid="volunteer-message" value={form.message} onChange={set("message")} rows={5} placeholder="Tell us about your skills, availability or organisation…" />
          </Field>
          <FormBanner status={banner.status} message={banner.message} testId="volunteer-banner" />
          <Btn testId="volunteer-submit" type="submit" disabled={banner.status === "loading"}>
            {banner.status === "loading" ? "Sending…" : "Count Me In"}
          </Btn>
        </form>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- Contact */

export function Contact() {
  const { page, form, errors, banner, set, submit } = useFormPage("contact", "/public/forms/contact");
  const { s } = useSite();
  if (page === null) return <Loading />;
  const b = page?.body_json || {};
  const hero = b.hero || {};

  const onSubmit = (ev) => {
    ev.preventDefault();
    submit(
      () => {
        const e = {};
        if (form.name.trim().length < 2) e.name = "Please enter your name.";
        if (!emailOk(form.email)) e.email = "Please enter a valid email address.";
        if (form.message.trim().length < 10) e.message = "Please enter your message.";
        return e;
      },
      {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
        consent: true,
        honeypot: form.honeypot,
      }
    );
  };

  const details = [
    { Icon: Phone, label: "Phone", value: s("site.phone"), href: `tel:${(s("site.phone") || "").replace(/\s/g, "")}` },
    { Icon: Mail, label: "Email", value: s("site.email"), href: `mailto:${s("site.email")}` },
    { Icon: MapPin, label: "Location", value: s("site.address") },
  ].filter((item) => String(item.value || "").trim());

  return (
    <>
      <Seo title={page?.seo_title} description={page?.seo_description} canonicalPath="/contact" />
      <PageHero
        overline={hero.overline}
        line1={hero.heading_line_1}
        subheading={hero.subheading}
        image={mediaUrl(page?.hero_image_url)}
      />
      <div className="bka-container grid gap-12 py-14 lg:grid-cols-[1.25fr_1fr] lg:gap-16 lg:py-20">
        <div>
          <SectionHead overline="Send a Message" title="Get In Touch" text={b.intro} />
          <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form" noValidate>
            <Honeypot value={form.honeypot} onChange={set("honeypot")} />
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Your Name" htmlFor="c-name" required error={errors.name}>
                <TextInput id="c-name" data-testid="contact-name" value={form.name} onChange={set("name")} error={errors.name} placeholder="Enter your full name" />
              </Field>
              <Field label="Email Address" htmlFor="c-email" required error={errors.email}>
                <TextInput id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@example.com" />
              </Field>
              <Field label="Phone Number" htmlFor="c-phone">
                <TextInput id="c-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="(242) 000-0000" />
              </Field>
              <Field label="Subject" htmlFor="c-subject">
                <TextInput id="c-subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="What is this about?" />
              </Field>
            </div>
            <Field label="Your Message" htmlFor="c-message" required error={errors.message}>
              <TextArea id="c-message" data-testid="contact-message" value={form.message} onChange={set("message")} error={errors.message} rows={6} placeholder="How can we help?" />
            </Field>
            <FormBanner status={banner.status} message={banner.message} testId="contact-banner" />
            <Btn testId="contact-submit" type="submit" disabled={banner.status === "loading"}>
              {banner.status === "loading" ? "Sending…" : "Send Message"}
            </Btn>
          </form>
        </div>

        <aside className="space-y-4">
          {details.map(({ Icon, label, value, href }) => (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`contact-detail-${label.toLowerCase()}`}>
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bka-aqua text-bka-emerald">
                <Icon className="h-4 w-4" />
              </span>
              <p className="bka-overline mt-4 text-bka-ink/50">{label}</p>
              {href ? (
                <a href={href} className="mt-1 block break-words text-sm font-bold text-bka-navy hover:text-bka-emerald">
                  {value}
                </a>
              ) : (
                <p className="mt-1 text-sm font-bold text-bka-navy">{value}</p>
              )}
            </div>
          ))}
          <div className="rounded-2xl bg-bka-navy p-6 text-white">
            <h3 className="font-display text-lg font-bold">Looking for support?</h3>
            <p className="mt-2 text-sm text-white/70">
              Use the dedicated support form so we can route your request to the right place.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Btn to="/get-support" testId="contact-support-cta">
                Get Support
              </Btn>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
